/**
 * Smithy (铁匠铺) - Weapon/armor crafting workshop
 * Features: Blue-gray curved roof, stone forge with fire outside (bottom-left), chimney with smoke, anvil
 * Size: 3x3 tiles (visual ~2.6 x 2.6)
 * Roof Height: ~2.4 (tallest among training buildings due to chimney)
 */

import * as THREE from 'three';

export function createSmithyMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.95 });
  const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.9 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.85 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const blueRoofMat = new THREE.MeshStandardMaterial({ color: 0x455a64, roughness: 0.7 });
  const blueEdgeMat = new THREE.MeshStandardMaterial({ color: 0x263238, roughness: 0.6 });
  const metalMat = new THREE.MeshStandardMaterial({ color: 0x424242, roughness: 0.4, metalness: 0.7 });
  const fireMat = new THREE.MeshStandardMaterial({
    color: 0xff5722,
    emissive: 0xff3d00,
    emissiveIntensity: 0.6
  });
  const smokeMat = new THREE.MeshStandardMaterial({
    color: 0xcccccc,
    transparent: true,
    opacity: 0.5,
    roughness: 1.0
  });
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.4
  });

  // === Stone Platform Base (2.6 x 2.6) ===
  const baseGeom = new THREE.BoxGeometry(2.6, 0.22, 2.6);
  const base = new THREE.Mesh(baseGeom, stoneMat);
  base.position.y = 0.11;
  base.receiveShadow = true;
  base.castShadow = true;
  group.add(base);

  // Edge detail
  const edgeGeom = new THREE.BoxGeometry(2.7, 0.06, 2.7);
  const edge = new THREE.Mesh(edgeGeom, new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 }));
  edge.position.y = 0.25;
  group.add(edge);

  // Front steps (centered)
  for (let i = 0; i < 2; i++) {
    const stepGeom = new THREE.BoxGeometry(0.65, 0.1, 0.18);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0.3, 0.05 + i * 0.1, 1.15 + i * 0.15);
    step.castShadow = true;
    group.add(step);
  }

  // === Main Building Structure ===
  // Back wall
  const backWallGeom = new THREE.BoxGeometry(1.7, 1.15, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0.15, 0.82, -0.95);
  backWall.castShadow = true;
  group.add(backWall);

  // Left wall
  const leftWallGeom = new THREE.BoxGeometry(0.12, 1.15, 1.6);
  const leftWall = new THREE.Mesh(leftWallGeom, darkWoodMat);
  leftWall.position.set(-0.65, 0.82, -0.2);
  leftWall.castShadow = true;
  group.add(leftWall);

  // Right wall
  const rightWallGeom = new THREE.BoxGeometry(0.12, 1.15, 1.2);
  const rightWall = new THREE.Mesh(rightWallGeom, darkWoodMat);
  rightWall.position.set(0.95, 0.82, -0.4);
  rightWall.castShadow = true;
  group.add(rightWall);

  // Front wall sections
  const frontWallLeftGeom = new THREE.BoxGeometry(0.45, 1.15, 0.12);
  const frontWallLeft = new THREE.Mesh(frontWallLeftGeom, darkWoodMat);
  frontWallLeft.position.set(-0.42, 0.82, 0.55);
  frontWallLeft.castShadow = true;
  group.add(frontWallLeft);

  const frontWallRightGeom = new THREE.BoxGeometry(0.4, 1.15, 0.12);
  const frontWallRight = new THREE.Mesh(frontWallRightGeom, darkWoodMat);
  frontWallRight.position.set(0.75, 0.82, 0.55);
  frontWallRight.castShadow = true;
  group.add(frontWallRight);

  // Top wall above door
  const frontWallTopGeom = new THREE.BoxGeometry(0.55, 0.35, 0.12);
  const frontWallTop = new THREE.Mesh(frontWallTopGeom, darkWoodMat);
  frontWallTop.position.set(0.27, 1.22, 0.55);
  group.add(frontWallTop);

  // === Support Pillars ===
  const pillarGeom = new THREE.BoxGeometry(0.1, 1.2, 0.1);
  const pillarPositions = [
    { x: -0.6, z: 0.6 },
    { x: 0.95, z: 0.2 },
    { x: -0.6, z: -0.9 },
    { x: 0.95, z: -0.9 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, woodMat);
    pillar.position.set(pos.x, 0.85, pos.z);
    pillar.castShadow = true;
    group.add(pillar);
  }

  // === Blue-Gray Curved Roof (Higher than others) ===
  const roofBaseGeom = new THREE.BoxGeometry(2.2, 0.12, 2.2);
  const roofBase = new THREE.Mesh(roofBaseGeom, blueRoofMat);
  roofBase.position.set(0.15, 1.5, -0.15);
  roofBase.castShadow = true;
  group.add(roofBase);

  // Curved roof top
  const roofConeGeom = new THREE.ConeGeometry(1.3, 0.75, 4);
  const roofCone = new THREE.Mesh(roofConeGeom, blueRoofMat);
  roofCone.position.set(0.15, 1.95, -0.15);
  roofCone.rotation.y = Math.PI / 4;
  roofCone.castShadow = true;
  group.add(roofCone);

  // Roof edge
  const roofEdgeGeom = new THREE.BoxGeometry(2.35, 0.06, 2.35);
  const roofEdge = new THREE.Mesh(roofEdgeGeom, blueEdgeMat);
  roofEdge.position.set(0.15, 1.45, -0.15);
  group.add(roofEdge);

  // Corner decorations
  const cornerGeom = new THREE.BoxGeometry(0.12, 0.06, 0.22);
  const cornerPositions = [
    { x: 1.2, z: 1.0, ry: -0.3 }, { x: -0.9, z: 1.0, ry: 0.3 },
    { x: 1.2, z: -1.25, ry: 0.3 }, { x: -0.9, z: -1.25, ry: -0.3 }
  ];
  for (const pos of cornerPositions) {
    const corner = new THREE.Mesh(cornerGeom, blueEdgeMat);
    corner.position.set(pos.x, 1.43, pos.z);
    corner.rotation.y = pos.ry;
    corner.rotation.z = 0.2;
    group.add(corner);
  }

  // === Stone Forge (Bottom-left corner, outside main building) ===
  // Forge platform
  const forgePlatformGeom = new THREE.BoxGeometry(0.8, 0.15, 0.8);
  const forgePlatform = new THREE.Mesh(forgePlatformGeom, darkStoneMat);
  forgePlatform.position.set(-0.95, 0.3, 0.9);
  group.add(forgePlatform);

  // Forge base
  const forgeBaseGeom = new THREE.BoxGeometry(0.6, 0.45, 0.5);
  const forgeBase = new THREE.Mesh(forgeBaseGeom, darkStoneMat);
  forgeBase.position.set(-0.95, 0.6, 0.85);
  forgeBase.castShadow = true;
  group.add(forgeBase);

  // Forge opening (front)
  const forgeOpeningGeom = new THREE.BoxGeometry(0.35, 0.3, 0.1);
  const forgeOpeningMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a });
  const forgeOpening = new THREE.Mesh(forgeOpeningGeom, forgeOpeningMat);
  forgeOpening.position.set(-0.95, 0.55, 1.1);
  group.add(forgeOpening);

  // Fire in forge
  const flame1Geom = new THREE.ConeGeometry(0.07, 0.18, 5);
  const flame1 = new THREE.Mesh(flame1Geom, fireMat);
  flame1.position.set(-0.98, 0.62, 0.95);
  group.add(flame1);

  const flame2Geom = new THREE.ConeGeometry(0.09, 0.22, 5);
  const flame2 = new THREE.Mesh(flame2Geom, fireMat);
  flame2.position.set(-0.88, 0.65, 0.92);
  group.add(flame2);

  // Glowing coals
  const coalGlowGeom = new THREE.SphereGeometry(0.1, 8, 6);
  const coalGlow = new THREE.Mesh(coalGlowGeom, fireMat);
  coalGlow.position.set(-0.95, 0.48, 0.95);
  group.add(coalGlow);

  // === Chimney (on forge) ===
  const chimneyGeom = new THREE.BoxGeometry(0.3, 1.3, 0.3);
  const chimney = new THREE.Mesh(chimneyGeom, darkStoneMat);
  chimney.position.set(-0.95, 1.45, 0.75);
  chimney.castShadow = true;
  group.add(chimney);

  // Chimney top
  const chimneyTopGeom = new THREE.BoxGeometry(0.4, 0.1, 0.4);
  const chimneyTop = new THREE.Mesh(chimneyTopGeom, stoneMat);
  chimneyTop.position.set(-0.95, 2.15, 0.75);
  group.add(chimneyTop);

  // === Smoke Particles ===
  for (let i = 0; i < 4; i++) {
    const size = 0.1 - i * 0.015;
    const smokeGeom = new THREE.SphereGeometry(size, 6, 4);
    const smoke = new THREE.Mesh(smokeGeom, smokeMat);
    smoke.position.set(
      -0.95 + (Math.random() - 0.5) * 0.12,
      2.3 + i * 0.18,
      0.75 + (Math.random() - 0.5) * 0.08
    );
    group.add(smoke);
  }

  // === Anvil (near door) ===
  // Anvil stump
  const stumpGeom = new THREE.CylinderGeometry(0.12, 0.14, 0.25, 8);
  const stumpMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.9 });
  const stump = new THREE.Mesh(stumpGeom, stumpMat);
  stump.position.set(0.5, 0.35, 0.85);
  group.add(stump);

  // Anvil body
  const anvilBaseGeom = new THREE.BoxGeometry(0.18, 0.2, 0.12);
  const anvilBase = new THREE.Mesh(anvilBaseGeom, metalMat);
  anvilBase.position.set(0.5, 0.58, 0.85);
  group.add(anvilBase);

  // Anvil top
  const anvilTopGeom = new THREE.BoxGeometry(0.28, 0.06, 0.14);
  const anvilTop = new THREE.Mesh(anvilTopGeom, metalMat);
  anvilTop.position.set(0.5, 0.71, 0.85);
  group.add(anvilTop);

  // Anvil horn
  const hornGeom = new THREE.ConeGeometry(0.035, 0.12, 6);
  const horn = new THREE.Mesh(hornGeom, metalMat);
  horn.position.set(0.66, 0.71, 0.85);
  horn.rotation.z = -Math.PI / 2;
  group.add(horn);

  // === Water Bucket (near anvil) ===
  const bucketGeom = new THREE.CylinderGeometry(0.08, 0.1, 0.15, 8);
  const bucketMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.8 });
  const bucket = new THREE.Mesh(bucketGeom, bucketMat);
  bucket.position.set(0.75, 0.3, 1.0);
  group.add(bucket);

  // Water
  const waterGeom = new THREE.CylinderGeometry(0.06, 0.08, 0.04, 8);
  const waterMat = new THREE.MeshStandardMaterial({ color: 0x4488aa, roughness: 0.3, transparent: true, opacity: 0.8 });
  const water = new THREE.Mesh(waterGeom, waterMat);
  water.position.set(0.75, 0.37, 1.0);
  group.add(water);

  // === Workbench (inside) ===
  const benchGeom = new THREE.BoxGeometry(0.55, 0.06, 0.3);
  const bench = new THREE.Mesh(benchGeom, woodMat);
  bench.position.set(-0.35, 0.48, -0.5);
  group.add(bench);

  // Bench legs
  const legGeom = new THREE.BoxGeometry(0.05, 0.22, 0.05);
  const legPositions = [
    { x: -0.55, z: -0.35 }, { x: -0.55, z: -0.65 },
    { x: -0.15, z: -0.35 }, { x: -0.15, z: -0.65 }
  ];
  for (const pos of legPositions) {
    const leg = new THREE.Mesh(legGeom, woodMat);
    leg.position.set(pos.x, 0.34, pos.z);
    group.add(leg);
  }

  // === Tools on Wall ===
  // Hammer on bench
  const hammerHeadGeom = new THREE.BoxGeometry(0.08, 0.06, 0.05);
  const hammerHead = new THREE.Mesh(hammerHeadGeom, metalMat);
  hammerHead.position.set(-0.45, 0.55, -0.5);
  group.add(hammerHead);

  const hammerHandleGeom = new THREE.CylinderGeometry(0.012, 0.015, 0.18, 6);
  const hammerHandle = new THREE.Mesh(hammerHandleGeom, woodMat);
  hammerHandle.position.set(-0.45, 0.55, -0.5);
  hammerHandle.rotation.z = Math.PI / 2;
  group.add(hammerHandle);

  // Tongs
  const tongGeom = new THREE.CylinderGeometry(0.01, 0.01, 0.25, 6);
  const tong1 = new THREE.Mesh(tongGeom, metalMat);
  tong1.position.set(-0.25, 0.54, -0.48);
  tong1.rotation.x = Math.PI / 2;
  group.add(tong1);

  // === Weapon Rack on Wall ===
  const rackGeom = new THREE.BoxGeometry(0.06, 0.4, 0.4);
  const rack = new THREE.Mesh(rackGeom, woodMat);
  rack.position.set(0.88, 0.65, -0.5);
  group.add(rack);

  // Swords on rack
  for (let i = 0; i < 2; i++) {
    const bladeGeom = new THREE.BoxGeometry(0.03, 0.45, 0.015);
    const blade = new THREE.Mesh(bladeGeom, new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.8, roughness: 0.3 }));
    blade.position.set(0.8, 0.65, -0.35 - i * 0.25);
    blade.rotation.z = 0.08;
    group.add(blade);

    const handleGeom = new THREE.BoxGeometry(0.04, 0.1, 0.025);
    const handle = new THREE.Mesh(handleGeom, woodMat);
    handle.position.set(0.8, 0.38, -0.35 - i * 0.25);
    handle.rotation.z = 0.08;
    group.add(handle);
  }

  // === Lantern ===
  // Lantern body
  const lanternBodyGeom = new THREE.BoxGeometry(0.1, 0.14, 0.1);
  const lanternBody = new THREE.Mesh(lanternBodyGeom, lanternMat);
  lanternBody.position.set(0.05, 1.25, 0.6);
  group.add(lanternBody);

  // Lantern top
  const lanternTopGeom = new THREE.ConeGeometry(0.07, 0.06, 4);
  const lanternTopMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const lanternTop = new THREE.Mesh(lanternTopGeom, lanternTopMat);
  lanternTop.position.set(0.05, 1.35, 0.6);
  lanternTop.rotation.y = Math.PI / 4;
  group.add(lanternTop);

  // === Hot Metal Piece on Anvil ===
  const hotMetalGeom = new THREE.BoxGeometry(0.12, 0.025, 0.05);
  const hotMetalMat = new THREE.MeshStandardMaterial({
    color: 0xff6600,
    emissive: 0xff4400,
    emissiveIntensity: 0.5
  });
  const hotMetal = new THREE.Mesh(hotMetalGeom, hotMetalMat);
  hotMetal.position.set(0.5, 0.76, 0.85);
  group.add(hotMetal);
}
